/* eslint-disable jsx-a11y/anchor-is-valid */
import { AddPhotoAlternate } from '@mui/icons-material';
import { Box, Link, Typography, useTheme } from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import { DROPZONE_CONFIG } from './constants';
import useImageValidation from './useImageValidation';

type PropsType = {
  error: SerializedError | null;
  onSuccess: (file: File) => void;
};

const Dropzone = ({ error, onSuccess }: PropsType) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { validationError, onDropAccepted, onDropRejected } =
    useImageValidation({ onSuccess });

  const hasError = useMemo(
    () => validationError || error,
    [error, validationError]
  );

  const errorMessage = validationError?.message ?? error?.message ?? '';

  const { isDragAccept, isDragReject, open, getRootProps, getInputProps } =
    useDropzone({
      ...DROPZONE_CONFIG,
      onDropAccepted,
      onDropRejected,
    });

  const isFocused = isDragAccept || isDragReject;

  const borderStyle = useMemo(() => {
    if (isFocused) {
      return { borderColor: theme.palette.common.white, border: '1px solid' };
    }
    if (hasError) {
      return { borderColor: theme.palette.error.main };
    }

    return { borderColor: theme.palette.grey[600] };
  }, [theme, isFocused, hasError]);

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          width: theme.spacing(45),
          height: theme.spacing(45),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px dashed',

          background: theme.palette.grey[700],
          gap: theme.spacing(1),
          ...borderStyle,
        }}
      >
        <input {...getInputProps()} />
        <AddPhotoAlternate
          sx={{
            width: theme.spacing(4),
            height: theme.spacing(4),
            color: isFocused
              ? theme.palette.common.white
              : theme.palette.grey[400],
          }}
        />

        {isFocused ? (
          <Typography
            sx={{
              color: theme.palette.common.white,
              fontSize: 12,
              lineHeight: 1.67,
            }}
          >
            {`${t('distribution:details:coverArt:hover')} `}
          </Typography>
        ) : (
          <Typography
            align="center"
            sx={{
              color: theme.palette.grey[400],
              fontSize: 12,
              lineHeight: 1.67,
            }}
          >
            <Trans i18nKey="distribution:details:coverArt:hint" t={t}>
              <Link
                onClick={open}
                sx={{
                  color: theme.palette.common.white,
                  textDecorationColor: theme.palette.common.white,
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
              />
            </Trans>
          </Typography>
        )}
      </Box>
      {hasError && (
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: 1.67,
            color: theme.palette.error.main,
          }}
        >
          {`${t('distribution:validation:coverArt:title')} `}
          <Typography
            sx={{
              fontSize: 12,
              color: theme.palette.error.main,
            }}
          >
            {errorMessage}
          </Typography>
        </Typography>
      )}
    </>
  );
};

export default Dropzone;
